import { appEnv, appVersion } from '@shared/constants/build'
import { type AxiosRequestConfig } from 'axios'
import { getAnonymousIdOnServer } from '@/features/shared/serverUtils/getAnonymousIdOnServer'
import { getAnonymousId } from '@shipt/analytics-member-web'
import { getUniversalId } from '@/features/shared/services/Experiments/utils'
import { type RequestCookies, type RequestHeaders } from '@shared/request_type'

type RequestHeaderParams = {
  suppressIdHeaders?: boolean
  isGql?: boolean
  cookies: RequestCookies | undefined
  headers: RequestHeaders | undefined
}

export const getCommonRequestHeaders = ({
  suppressIdHeaders = false,
  isGql = false,
  cookies: requestCookies,
  headers: requestHeaders,
}: RequestHeaderParams) => {
  const universalId = getUniversalId({ serverCookies: requestCookies })
  const anonymousId = requestCookies ? getAnonymousIdOnServer(requestCookies) : getAnonymousId()
  const headers: AxiosRequestConfig['headers'] = {
    'x-shipt-identifier': 'segway',
    'Content-Type': 'application/json',
    ...(isGql && {
      'apollographql-client-name': `web-shipt-${appEnv}`,
      'apollographql-client-version': appVersion,
    }),
    ...(isGql &&
      !suppressIdHeaders && {
        'x-anonymous-id': anonymousId,
        ...(universalId && { 'x-universal-id': universalId }),
      }),
  }
  if (requestHeaders && process.env.SHIPT_SEGWAY_INTERNAL_VALUE) {
    // pass internal header and value for internal api requests
    headers['shipt-segway-internal'] = process.env.SHIPT_SEGWAY_INTERNAL_VALUE
  }
  // pass along fastly-ray-id for request tracing in logs
  if (requestHeaders?.['fastly-ray-id']) {
    headers['fastly-ray-id'] = requestHeaders['fastly-ray-id']
  }
  // pass along user-agent to help downstream services debug
  if (requestHeaders) {
    headers['X-Browser-User-Agent'] = requestHeaders['user-agent'] ?? ''
    headers['X-Shipt-Segway-SSR'] = 'true'
  }

  // These headers provide context for network requests
  // made during Cypress runs in Circle CI
  if (process.env.NEXT_PUBLIC_ENV === 'local' && !isGql) {
    headers['x-ci_workflow_id'] = process.env.NEXT_PUBLIC_CIRCLE_WORKFLOW_ID ?? 'local'
    headers['x-ci_job_id'] = process.env.NEXT_PUBLIC_CIRCLE_WORKFLOW_JOB_ID ?? 'local'
    headers['x-ci_step_index'] = process.env.NEXT_PUBLIC_CIRCLE_NODE_INDEX ?? 'local'
  }

  return headers
}
